import React, { useEffect, useState } from "react";
import { authService } from "../services/auth.service";
import { capabilitiesService } from "../services/capabilities.service";
import SubscriptionSection from "../components/SubscriptionSection";
import "../styles/MyAccountPage.css";
import { getErrorMessage } from '../utils/error';
import { PASSWORD_DESCRIPTION, PASSWORD_MIN_LENGTH, passwordError } from '../utils/passwordPolicy';

const MyAccountPage: React.FC = () => {
  const user = authService.getCurrentUser()?.user;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // null = pas encore connu. Le statut vient de l'API et non de l'utilisateur
  // stocké : le lien de vérification est souvent ouvert dans un autre
  // navigateur, et la session locale resterait périmée jusqu'à reconnexion.
  const [verified, setVerified] = useState<boolean | null>(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  useEffect(() => {
    capabilitiesService
      .getCapabilities()
      .then((c) => setVerified(c.emailVerified))
      .catch(() => setVerified(null)); // en cas d'échec, on n'affiche rien
  }, []);

  const handleResend = async () => {
    setResending(true);
    setResendMsg(null);
    try {
      const r = await authService.resendVerification();
      if (r.alreadyVerified) {
        setVerified(true);
      } else {
        setResendMsg(
          r.success
            ? "Email envoyé. Pensez à regarder dans vos indésirables."
            : "L'envoi a échoué. Réessayez dans quelques minutes.",
        );
      }
    } catch (err) {
      setResendMsg(getErrorMessage(err, "L'envoi a échoué. Réessayez."));
    } finally {
      setResending(false);
    }
  };


  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Les mots de passe ne correspondent pas" });
      return;
    }
    // La règle est celle du backend. Le contrôle local exigeait SIX caractères
    // quand `PUT /api/auth/change-password` en exige huit avec majuscule et
    // caractère spécial : le formulaire acceptait ce que l'API refusait.
    const probleme = passwordError(newPassword);
    if (probleme) {
      setMessage({ type: "error", text: probleme });
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setMessage({ type: "success", text: "Mot de passe modifié avec succès" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = getErrorMessage(err, "Erreur lors du changement de mot de passe");
      setMessage({ type: "error", text: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-container">
      <h2>Mon compte</h2>

      <div className="account-section">
        <h3>Informations personnelles</h3>
        <div className="account-info">
          <div className="info-row">
            <span className="info-label">Nom</span>
            <span className="info-value">{user?.firstName} {user?.lastName}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Email</span>
            <span className="info-value">
              {user?.email}
              {verified === null ? null : verified ? (
                <span className="email-badge verified">Vérifiée</span>
              ) : (
                <span className="email-badge unverified">Non vérifiée</span>
              )}
            </span>
          </div>
          {verified === false && (
            <div className="email-verify-note">
              <p>
                Confirmez votre adresse pour pouvoir mettre le site de votre
                colocation en ligne. Le lien vous a été envoyé à l&apos;inscription.
              </p>
              <button
                type="button"
                className="submit-button"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? "Envoi…" : "Renvoyer l'email de vérification"}
              </button>
              {resendMsg && <p className="email-verify-feedback">{resendMsg}</p>}
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Rôle</span>
            <span className="info-value">{user?.role === "OWNER" ? "Propriétaire" : user?.role}</span>
          </div>
        </div>
      </div>

      <SubscriptionSection />

      <div className="account-section">
        <h3>Changer le mot de passe</h3>
        <form onSubmit={handleChangePassword} className="password-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Mot de passe actuel</label>
            <input
              id="currentPassword"
              autoComplete="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">Nouveau mot de passe</label>
            <input
              id="newPassword"
              autoComplete="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
            />
            {/* La règle est annoncée AVANT la saisie : la découvrir par un
                refus après coup fait retaper deux fois. */}
            <p className="account-hint">{PASSWORD_DESCRIPTION}</p>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
            <input
              id="confirmPassword"
              autoComplete="new-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          {message && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? "Modification..." : "Modifier le mot de passe"}
          </button>
        </form>
      </div>

    </div>
  );
};

export default MyAccountPage;
